import { Check, Minus } from "lucide-react";
import { type InputHTMLAttributes, forwardRef, useEffect, useRef } from "react";
import { cn } from "../cn";

export interface CheckboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  readonly indeterminate?: boolean;
}

/** Native checkbox semantics with the shared control styling. */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, indeterminate = false, disabled, onChange, ...props }, ref) => {
    const inputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
      if (inputRef.current) {
        inputRef.current.indeterminate = indeterminate;
      }
    });

    const Icon = indeterminate ? Minus : Check;

    return (
      <span
        className={cn(
          "relative inline-grid size-4 shrink-0 place-items-center",
          disabled && "opacity-50",
        )}
      >
        <input
          ref={(input) => {
            inputRef.current = input;
            if (typeof ref === "function") {
              ref(input);
            } else if (ref) {
              ref.current = input;
            }
          }}
          type="checkbox"
          disabled={disabled}
          className={cn(
            "peer size-4 shrink-0 appearance-none rounded-sm border border-input bg-surface shadow-xs transition-colors checked:border-primary checked:bg-primary indeterminate:border-primary indeterminate:bg-primary hover:border-foreground/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none",
            className,
          )}
          {...props}
          onChange={(event) => {
            onChange?.(event);
            // Native clicks clear mixed state even when the caller keeps it set.
            event.currentTarget.indeterminate = indeterminate;
          }}
        />
        <Icon
          aria-hidden="true"
          className="pointer-events-none absolute size-3 text-primary-foreground opacity-0 transition-opacity peer-checked:opacity-100 peer-indeterminate:opacity-100"
        />
      </span>
    );
  },
);
Checkbox.displayName = "Checkbox";
